import clsx from "clsx";
import Button from "components/Button";
import Tooltip from "components/Tooltip";
import { useClickOutside } from "hooks/useClickOutside";
import { IconDownload } from "icons";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DOWNLOAD_SETTING, DOWNLOAD_AI_LABEL_SETTING, useLocalStorage } from "hooks/useLocalStorage";
import Portal from "components/Portal";
import { getFilenameFromUrl } from "./utils";
import { DownloadSubject, resolveDownloadBaseName } from "./downloadName";
import { DownloadResolution, FileTypeOption, FILE_TYPE_OPTIONS, VIDEO_FILE_TYPE_OPTION } from "./constants";
import DownloadMenu from "./components/DownloadMenu";
import DownloadProgressPopup from "./components/DownloadProgressPopup";
import { useDownloadLogic } from "./hooks/useDownloadLogic";
import { useImageDimensions } from "./hooks/useImageDimensions";
import { useMenuPosition } from "./hooks/useMenuPosition";

interface DownloadButtonProps {
  isDisplayed: boolean;
  imageId?: string;
  designId?: string;
  isImageLandscape?: boolean;
  position?: "top" | "bottom" | "left" | "right";
  imageURL?: string;
  onIconClick?: () => void;
  isSvgAvailable?: boolean;
  svgComingSoon?: boolean;
  disabled?: boolean;
  isVideo?: boolean;
  showDownloadMenu?: boolean;
  isTechpackDownload?: boolean;
  // Which feature/surface fired the download — logged as `surface` (REF-5664) so the backoffice
  // can show a Generation Type. Defaults to "download_button"; feature call sites pass a specific
  // value (e.g. "photoshoot_tryon", "design_canvas") mapped to a label in the backoffice.
  analyticsSurface?: string;
  // REF-6450: WHAT the frame is, where analyticsSurface says WHERE. Studio / Edit Design pass the
  // frame's `StudioImageType` (tryon / ghost / pose_edit / polish …); it travels as `sub_type` in the
  // download-log ping. Leave it unset on surfaces that don't know — the backoffice falls back to its
  // own reconstruction for a NULL, but shows an unknown string as-is.
  analyticsSubType?: string;
  // Which product this render belongs to — `{sku, slot}`. With it the name field is prefilled
  // `<SKU>_<slot>` instead of the CDN object name, which is also what the bulk ZIP produces for
  // the same image. Optional: a surface with no product behind it (design canvas, tech pack
  // artwork) sends nothing and keeps the name it has today. See ./downloadName.
  subject?: DownloadSubject | null;
  // Fired when the menu opens/closes. Hover-reveal grids (PhotoshootsItem) use it to keep their
  // toolbar + this control mounted while the menu is open, so moving the cursor to the (portaled)
  // menu doesn't drop the card hover and close it. Mirrors DeleteButton's isOpen/setIsOpen lift.
  onMenuToggle?: (open: boolean) => void;
  // Replaces the default 26px white square trigger — e.g. the round dark hover controls on the
  // assets-style grid cards. `triggerIconClassName` sizes the glyph to match.
  triggerClassName?: string;
  triggerIconClassName?: string;
  // Text after the icon (a labelled action button rather than an icon chip) and a full-width wrapper, for
  // action lists like the image detail panel. A labelled trigger drops the redundant "Download" tooltip.
  triggerLabel?: React.ReactNode;
  fullWidth?: boolean;
}

const DownloadButton: React.FC<DownloadButtonProps> = ({
  isDisplayed,
  imageId,
  designId,
  isImageLandscape,
  position,
  imageURL,
  onIconClick,
  isSvgAvailable = false,
  svgComingSoon = false,
  disabled,
  isVideo,
  showDownloadMenu,
  isTechpackDownload,
  analyticsSurface = "download_button",
  analyticsSubType,
  subject,
  onMenuToggle,
  triggerClassName,
  triggerIconClassName,
  triggerLabel,
  fullWidth = false,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const downloadButtonRef = useRef<HTMLDivElement>(null);

  const [showMenu, setShowMenu] = useState(false);
  const [fileType, setFileType] = useState<FileTypeOption>(() => {
    if (isVideo) {
      return VIDEO_FILE_TYPE_OPTION;
    }
    const savedFileType = useLocalStorage().getItem(DOWNLOAD_SETTING);
    const availableOptions = isSvgAvailable ? FILE_TYPE_OPTIONS : FILE_TYPE_OPTIONS.filter((option) => option.value !== "svg");

    // If saved option is SVG but it's coming soon, fallback to first available option
    if (savedFileType === "svg" && svgComingSoon) {
      return availableOptions.filter((option) => option.value !== "svg")[0];
    }

    return availableOptions.find((option) => option.value === savedFileType) || availableOptions[0];
  });

  const [resolution, setResolution] = useState<DownloadResolution>("2k");

  // REF-5701: "Include AI Generated" toggle — persisted (default off) so opting in sticks without
  // making every download a BE round-trip for users who don't want the label.
  const [aiLabel, setAiLabel] = useState<boolean>(() => useLocalStorage().getItem(DOWNLOAD_AI_LABEL_SETTING) === true);

  const handleAiLabelChange = (checked: boolean) => {
    setAiLabel(checked);
    useLocalStorage().setItem(DOWNLOAD_AI_LABEL_SETTING, checked);
  };

  // Editable base name (no extension), prefilled from the current image URL and
  // re-prefilled whenever the image changes. Falls back to "image" when the URL
  // yields no usable base. The extension is always derived from the selected
  // format at download time (see useDownloadLogic), so it's not stored here.
  const [filename, setFilename] = useState("");

  useEffect(() => {
    // `<SKU>_<slot>` when the caller said which product this is, else the CDN object name — the
    // SAME rule the download itself applies, so the field shows the name the file will get.
    //
    // This prefill is why the naming rule has to reach HERE and not only `useDownloadLogic`: the
    // field is never empty, so the hook always sees a "user-typed" name and its own fallback
    // would never be consulted. Prefilling the CDN name and resolving the SKU downstream would
    // have shipped a feature that could not fire.
    const fromSubject = resolveDownloadBaseName(undefined, subject);
    // The extension is stripped here and re-applied from the selected format at download time,
    // so fileType is intentionally not a dep.
    const fromUrl = imageURL ? getFilenameFromUrl(imageURL, "png").replace(/\.[^/.]+$/, "") : "";
    setFilename(fromSubject || fromUrl || "image");
  }, [imageURL, subject?.sku, subject?.slot]);

  const { isDownloading, showProgressPopup, download } = useDownloadLogic({
    fileType,
    designId,
    imageId,
    imageURL,
    showDownloadMenu,
    isTechpackDownload,
    resolution,
    analyticsSurface,
    analyticsSubType,
    filename,
    aiLabel,
    subject,
  });

  // Measure the source image lazily, only while the menu is open and the 4K control is actually
  // shown (matches DownloadMenu's showResolution gate: png/jpg, not techpack, AI label off).
  // Drives the Original (W×H) label + the already-4K disable.
  const showFourK = (fileType.value === "png" || fileType.value === "jpg") && !isTechpackDownload && !aiLabel;
  const imageDimensions = useImageDimensions(imageURL, showMenu && showFourK);

  // The upscale option is disabled for already-4K images — make sure it can't stay the active
  // selection, so the Download button performs the Original download instead of a no-op upscale.
  React.useEffect(() => {
    if (imageDimensions.isAlready4k && resolution === "4k") {
      setResolution("2k");
    }
  }, [imageDimensions.isAlready4k, resolution]);

  const menuStyle = useMenuPosition(showMenu, downloadButtonRef, menuRef);

  useClickOutside(menuRef, () => {
    setShowMenu(false);
  });

  React.useEffect(() => {
    if (!isDisplayed) {
      setShowMenu(false);
    }
  }, [isDisplayed]);

  React.useEffect(() => {
    onMenuToggle?.(showMenu);
  }, [showMenu, onMenuToggle]);

  React.useEffect(() => {
    if (isVideo) {
      setFileType(VIDEO_FILE_TYPE_OPTION);
    } else {
      const savedFileType = useLocalStorage().getItem(DOWNLOAD_SETTING);
      const availableOptions = isSvgAvailable ? FILE_TYPE_OPTIONS : FILE_TYPE_OPTIONS.filter((option) => option.value !== "svg");

      // If saved option is SVG but it's coming soon, fallback to first available option
      if (savedFileType === "svg" && svgComingSoon) {
        setFileType(availableOptions.filter((option) => option.value !== "svg")[0]);
      } else {
        setFileType(availableOptions.find((option) => option.value === savedFileType) || availableOptions[0]);
      }
    }
  }, [isVideo, isSvgAvailable, svgComingSoon]);

  async function handleDownload() {
    await download();
    setShowMenu(false);
  }

  return (
    <div className={clsx("relative", fullWidth && "w-full", disabled ? "pointer-events-none" : "")} ref={downloadButtonRef}>
      <Tooltip content={t("common:tooltips.download")} position={position ? position : isImageLandscape ? "top" : "left"} active={!triggerLabel} className={fullWidth ? "!block w-full" : undefined}>
        <Button
          disabled={disabled}
          aria-label={isDownloading ? t("common:tooltips.downloading_aria") : t("common:tooltips.download_file_aria")}
          aria-expanded={showMenu}
          aria-haspopup="menu"
          className={
            triggerClassName
              ? clsx(triggerClassName, showMenu && "pointer-events-none")
              : clsx(
                  "!bg-trueWhite p-2 hover:bg-green w-[26px] h-[26px] rounded-[3px] border border-black-200 dark:border-none disabled:!bg-black-300 disabled:!text-black-100 disabled:border-none",
                  showMenu ? "!bg-green pointer-events-none" : "!bg-trueWhite"
                )
          }
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            if (onIconClick) onIconClick();
            if (showDownloadMenu) {
              setShowMenu(true);

              return;
            }
            if (imageURL) {
              download();
            } else {
              setShowMenu(true);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape" && showMenu) {
              setShowMenu(false);
            }
          }}
        >
          <IconDownload className={clsx(triggerIconClassName ?? "w-4 h-4", "flex-shrink-0")} />
          {triggerLabel}
        </Button>
      </Tooltip>

      {showMenu && (
        <Portal>
          <div>
            <div
              ref={menuRef}
              // Keep the menu above detail modals and image-preview overlays. The latter also use
              // z-[100000], so the menu needs the next layer to remain interactive when opened there.
              className={clsx("fixed z-[100001] w-[270px]", !menuStyle.ready && "invisible")}
              style={{ top: `${menuStyle.top}px`, left: `${menuStyle.left}px` }}
              data-download-menu
              role="menu"
              aria-label="Download options"
              onClick={(e) => {
                e.stopPropagation();
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setShowMenu(false);
                }
              }}
            >
              <DownloadMenu
                setFileType={setFileType}
                fileType={fileType}
                onDownload={handleDownload}
                isDownloading={isDownloading}
                isSvgAvailable={isSvgAvailable ?? false}
                svgComingSoon={svgComingSoon}
                isVideo={isVideo ?? false}
                resolution={resolution}
                setResolution={setResolution}
                isTechpackDownload={isTechpackDownload ?? false}
                imageDimensions={imageDimensions}
                filename={filename}
                setFilename={setFilename}
                aiLabel={aiLabel}
                onAiLabelChange={handleAiLabelChange}
              />
            </div>
          </div>
        </Portal>
      )}

      {/* SVG → "preparing SVG" copy; 4K raster → "Enhancing resolution" copy (DownloadProgressPopup picks by fileType) */}
      {showProgressPopup && <DownloadProgressPopup imageURL={imageURL} fileType={fileType.value} />}
    </div>
  );
};

export default DownloadButton;
